'use client';

import { FC, useMemo, useState, VideoHTMLAttributes } from 'react';
import { twMerge } from '@tuic/tailwind-config';
import { Image, ImageProps, ImageSizes } from '../../Image';
import { resolveVideo, resolvePoster, resolvedVideoIsNative } from '../utils';
import { PosterResolverReturnValue, VideoPostResolver } from '../types';
import { ConsentCategory, useConsentCategories } from '@tuic/tracking';
import { NativeVideo } from './NativeVideo';
import { YouTubeVideo } from './YouTubeVideo';
import { applyContentfulCdnProxyIfEnabled, type I18NFromComponent } from '../../../../utils';
import { CookieConsentFallback } from '../../CookieConsentFallback';
import { VideoComponent } from '../../VideoComponent';
type VideoElementAttributes = Omit<VideoHTMLAttributes<HTMLVideoElement>, 'poster'>;
export type VideoProps = VideoElementAttributes & {
  poster?: ImageProps;
  posterSizes?: ImageSizes;
  i18n?: I18NFromComponent<typeof CookieConsentFallback>;
  postResolveVideo?: VideoPostResolver;
};
const CONSENT_CATEGORY: ConsentCategory = 'affiliates';
const renderPoster = (poster: PosterResolverReturnValue | undefined, sizes?: ImageSizes) => {
  if (typeof poster === 'undefined') {
    return null;
  }
  return <Image alt="" {...poster} className="inset-0 z-[1] absolute aspect-auto h-full w-full" sizes={sizes || poster.sizes} data-sentry-element="Image" data-sentry-component="renderPoster" data-sentry-source-file="Video.tsx" />;
};

/**
 * The Video component aims to streamline the embedding of videos from various services.
 * Currently, the component is able to handle YouTube & video file sources through the native `video` element.
 *
 * Make sure to provide a poster image when supplying a native video file in the src attribute to avoid any unnecessary video prefetching.
 * In case of a YouTube URL in the src attribute, poster image are derived automatically if you omit them.
 */
export const Video: FC<VideoProps> = props => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    className,
    i18n,
    poster,
    posterSizes,
    ...attrs
  } = props;
  const [userPlay, setUserPlay] = useState(false);
  const {
    hasConsent,
    setConsent
  } = useConsentCategories();
  const [showPoster, setShowPoster] = useState(true);
  const posterData = useMemo(() => resolvePoster(props), [props]);
  const videoData = useMemo(() => resolveVideo(props), [props]);
  const consentGiven = hasConsent(CONSENT_CATEGORY);
  const consentRequired = videoData && 'consentRequired' in videoData ? videoData.consentRequired : false;
  const consentValid = consentRequired !== true || consentRequired && consentGiven;
  const handleClick = () => {
    setUserPlay(true);
    setShowPoster(false);
  };
  const handleConsentClick = () => {
    setConsent(CONSENT_CATEGORY, true);
    handleClick();
  };
  if (resolvedVideoIsNative(videoData)) {
    return <div className={twMerge('aspect-16/9', className, 'relative overflow-hidden')}>
        {showPoster && renderPoster(posterData, posterSizes)}
        <div className="absolute inset-0 bg-black">
          <NativeVideo {...attrs} posterData={undefined} videoProps={videoData.video} userPlay={userPlay} autoPlay={props.autoPlay} />
        </div>
        {props.autoPlay !== true && !userPlay && <VideoComponent asChild>
            <button className="inset absolute block z-[2] h-full w-full" onClick={handleClick} />
          </VideoComponent>}
      </div>;
  }
  return <div className={twMerge('aspect-16/9', className, 'relative overflow-hidden')} data-sentry-component="Video" data-sentry-source-file="Video.tsx">
      {showPoster && renderPoster(posterData, posterSizes)}
      {!consentValid && <div className="absolute inset-0 flex z-[2] items-center justify-center">
          <CookieConsentFallback onClick={handleConsentClick} i18n={i18n} />
        </div>}
      {consentValid && props.autoPlay !== true && !userPlay && <VideoComponent asChild>
          <button className="inset absolute block z-[3] h-full w-full" onClick={handleClick} />
        </VideoComponent>}
      {consentValid && (props.autoPlay || userPlay) && <div className="absolute inset-0 bg-black transition-opacity duration-300 data-[loaded=true]:opacity-100">
          <YouTubeVideo iframeProps={videoData?.iframe} />
        </div>}
    </div>;
};